<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth, useUser, useClerk } from '@clerk/vue'
import AvatarDisplay from './AvatarDisplay.vue'
import ShareButton from './ShareButton.vue'
import SoundToggle from './SoundToggle.vue'
import ConfirmModal from './ConfirmModal.vue'
import { useAudio } from '@/composables/useAudio'
import { useGameState } from '@/composables/useGameState'
import { useDevice } from '@/composables/useDevice'
import { getApiUrl } from '@/utils/api'

const router = useRouter()
const route = useRoute()
const { isSignedIn } = useAuth()
const clerk = useClerk()
const { user } = useUser()

// Audio controls for game room
const { soundEnabled, toggleSound } = useAudio()
const { isMobile } = useDevice()

const isMenuOpen = ref(false)
const isProfileMenuOpen = ref(false)
const showLeaveConfirmModal = ref(false)

// Game state for leave confirmation
const { isGameActive } = useGameState()

// We need to fetch user profile data from our backend since Clerk doesn't store our custom fields directly
const userProfile = ref<{ handle: string, avatarSeed: string } | null>(null)

const avatarSeed = computed(() => {
  return userProfile.value?.avatarSeed || ''
})

const userHandle = computed(() => {
  return userProfile.value?.handle || ''
})

// Function to fetch user profile data
async function fetchUserProfile() {
  if (!isSignedIn.value || !clerk.value) return
  
  try {
    const session = await clerk.value.session
    if (!session) {
      console.error('[NavBar] No active session found')
      return
    }
    
    const token = await session.getToken()
    const response = await fetch(getApiUrl('/api/users/me'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      console.error('[NavBar] Failed to get profile:', response.status)
      return
    }
    
    userProfile.value = await response.json() as { handle: string, avatarSeed: string }
    console.log('[NavBar] Fetched user profile:', userProfile.value)
  } catch (error) {
    console.error('[NavBar] Error fetching user profile:', error)
  }
}

// Initialize user profile data
onMounted(() => {
  console.log('[NavBar] Component mounted, user loaded:', user.value ? 'yes' : 'no')
  if (isSignedIn.value) {
    fetchUserProfile()
  }
})

// Watch for authentication state changes
watch(() => isSignedIn.value, (isSignedIn) => {
  console.log('[NavBar] Auth state changed, signed in:', isSignedIn)
  if (isSignedIn) {
    fetchUserProfile()
  } else {
    userProfile.value = null
  }
}, { immediate: true })

const isGameRoom = computed(() => {
  return route.path.startsWith('/room/')
})

const roomCode = computed(() => {
  return isGameRoom.value ? route.params.code as string : ''
})

const handleLeave = () => {
  if (isGameActive.value) {
    showLeaveConfirmModal.value = true
  } else {
    router.push('/play')
    closeMenus()
  }
}

const confirmLeave = () => {
  showLeaveConfirmModal.value = false
  router.push('/play')
  closeMenus()
}

const cancelLeave = () => {
  showLeaveConfirmModal.value = false
}

const handleSignOut = async () => {
  if (clerk.value) {
    await clerk.value.signOut()
    router.push('/')
    isProfileMenuOpen.value = false
  }
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    isProfileMenuOpen.value = false
  }
}

const toggleProfileMenu = () => {
  isProfileMenuOpen.value = !isProfileMenuOpen.value
}

const closeMenus = () => {
  isMenuOpen.value = false
  isProfileMenuOpen.value = false
}
</script>

<template>
  <header :class="[
    'relative z-20',
    isGameRoom ? 'bg-gray-800' : 'bg-[#0c3b5f]',
    'shadow-md'
  ]">
    <div class="max-w-7xl mx-auto px-4" :class="isGameRoom && isMobile ? 'py-2' : 'py-4'">
      <div class="flex items-center justify-between">
        <!-- Logo and brand -->
        <div class="flex items-center">
          <router-link @click="closeMenus" :to="{ path: '/' }" class="flex items-center">
            <span class="mr-2">
              <!-- Small stack icon -->
              <div class="w-6 h-6 relative">
                <div class="w-6 h-1 bg-green-500 absolute top-0 rounded-sm"></div>
                <div class="w-6 h-1 bg-green-500 absolute top-2 rounded-sm"></div>
                <div class="w-6 h-1 bg-green-500 absolute top-4 rounded-sm"></div>
              </div>
            </span>
            <span class="font-bold text-white" :class="isGameRoom && isMobile ? 'text-lg' : 'text-2xl'">Stack25</span>
          </router-link>
          
          <!-- Room code display (only in game rooms) -->
          <span v-if="isGameRoom" class="ml-4 text-gray-400 text-sm hidden sm:inline-block">
            Room: {{ roomCode }}
          </span>
        </div>

        <!-- Desktop navigation -->
        <nav class="hidden md:flex items-center gap-6">
          <!-- Game room specific controls -->
          <template v-if="isGameRoom">
            <SoundToggle :enabled="soundEnabled" @toggle="toggleSound" />
            <ShareButton :room-code="roomCode" />
            <button @click="handleLeave" class="text-gray-400 hover:text-white text-sm font-medium transition duration-150">
              Leave
            </button>
          </template>
          
          <!-- Standard navigation links (hidden in game rooms) -->
          <template v-else>
            <router-link 
              @click="closeMenus"
              to="/play" 
              class="text-white border border-white py-2 px-4 rounded-md font-medium transition duration-150 hover:bg-[#0a2e4b]"
              :class="{ 'bg-[#0a2e4b]': route.path === '/play' }"
            >
              Play
            </router-link>
            <router-link 
              @click="closeMenus"
              to="/leaderboard" 
              class="text-white border border-white py-2 px-4 rounded-md font-medium transition duration-150 hover:bg-[#0a2e4b]"
              :class="{ 'bg-[#0a2e4b]': route.path === '/leaderboard' }"
            >
              Leaderboard
            </router-link>
          </template>
          
          <template v-if="isSignedIn">
            <!-- Avatar with dropdown for authenticated users -->
            <div class="relative">
              <button 
                @click="toggleProfileMenu"
                class="flex items-center focus:outline-none"
                aria-expanded="false"
              >
                <div class="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-200">
                  <AvatarDisplay v-if="avatarSeed" :seed="avatarSeed" :size="32" />
                  <div v-else class="w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs">
                    ?
                  </div>
                </div>
                <svg class="w-4 h-4 ml-1 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              <!-- Profile dropdown menu -->
              <div 
                v-show="isProfileMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
              >
                <div class="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                  Signed in as <span class="font-medium">{{ userHandle }}</span>
                </div>
                <router-link 
                  @click="closeMenus"
                  to="/profile" 
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Profile
                </router-link>
                <button 
                  @click="handleSignOut" 
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Sign out
                </button>
              </div>
            </div>
          </template>
          
          <template v-else>
            <router-link 
              @click="closeMenus"
              to="/auth/sign-in" 
              class="text-white px-4 py-2 rounded-md font-medium transition duration-150 border border-white hover:bg-[#0a2e4b]"
            >
              Sign In
            </router-link>
            <router-link 
              v-if="route.path === '/'"
              @click="closeMenus"
              to="/auth/sign-up" 
              class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition duration-150 border border-white"
            >
              Sign Up
            </router-link>
          </template>
        </nav>
        
        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center">
          <button 
            @click="toggleMenu"
            class="text-gray-200 hover:text-white focus:outline-none"
            aria-expanded="false"
          >
            <svg v-if="!isMenuOpen" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile menu -->
    <div 
      v-show="isMenuOpen"
      class="md:hidden bg-[#0a2e4b] shadow-inner"
      :class="{ 'bg-gray-700': isGameRoom }"
    >
      <div class="px-2 pt-2 pb-3 space-y-1">
        <!-- Game room specific controls for mobile -->
        <template v-if="isGameRoom">
          <div class="flex items-center justify-between px-3 py-2">
            <SoundToggle :enabled="soundEnabled" @toggle="toggleSound" />
            <ShareButton :room-code="roomCode" class="ml-2" />
          </div>
          <button 
            @click="handleLeave" 
            class="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-200 hover:text-white hover:bg-gray-600"
          >
            Leave Game
          </button>
        </template>
        
        <!-- Standard navigation links (hidden in game rooms) -->
        <template v-else>
          <router-link 
            @click="closeMenus"
            to="/play" 
            class="block px-3 py-2 rounded-md text-base font-medium text-white border border-white hover:bg-[#0a2e4b] mx-2 my-1 text-center"
            :class="[
              route.path === '/play' ? 'bg-[#0a2e4b]' : ''
            ]"
          >
            Play
          </router-link>
          <router-link 
            @click="closeMenus"
            to="/leaderboard" 
            class="block px-3 py-2 rounded-md text-base font-medium text-white border border-white hover:bg-[#0a2e4b] mx-2 my-1 text-center"
            :class="[
              route.path === '/leaderboard' ? 'bg-[#0a2e4b]' : ''
            ]"
          >
            Leaderboard
          </router-link>
        </template>
        
        <template v-if="isSignedIn">
          <router-link 
            @click="closeMenus"
            to="/profile" 
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-200 hover:text-white hover:bg-[#0c3b5f]"
            :class="[
              route.path === '/profile' ? 'bg-[#0c3b5f] text-white' : '',
              isGameRoom ? 'hover:bg-gray-600' : ''
            ]"
          >
            Profile
          </router-link>
          <div class="px-3 py-2">
            <div class="flex items-center">
              <div class="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-200 mr-2">
                <AvatarDisplay v-if="avatarSeed" :seed="avatarSeed" :size="32" />
                <div v-else class="w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs">
                  ?
                </div>
              </div>
              <span class="text-gray-200">{{ userHandle }}</span>
            </div>
          </div>
          <button 
            @click="handleSignOut" 
            class="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-200 hover:text-white hover:bg-[#0c3b5f]"
            :class="{ 'hover:bg-gray-600': isGameRoom }"
          >
            Sign out
          </button>
        </template>
        
        <template v-else>
          <router-link 
            @click="closeMenus"
            to="/auth/sign-in" 
            class="block px-3 py-2 rounded-md text-base font-medium text-white border border-white hover:bg-[#0a2e4b] mx-2 my-1 text-center"
          >
            Sign In
          </router-link>
          <router-link 
            v-if="route.path === '/'"
            @click="closeMenus"
            to="/auth/sign-up" 
            class="block px-3 py-2 rounded-md text-base font-medium text-white bg-green-600 hover:bg-green-700 border border-white mx-2 my-1 text-center"
          >
            Sign Up
          </router-link>
        </template>
      </div>
    </div>
    
    <!-- Leave confirmation modal -->
    <ConfirmModal
      :show="showLeaveConfirmModal"
      title="Leave Game"
      message="Are you sure you want to leave the game? This will end the current match."
      confirm-text="Leave Game"
      cancel-text="Stay"
      @confirm="confirmLeave"
      @cancel="cancelLeave"
    />
  </header>
</template>
